import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, CheckCircle2 } from "lucide-react";
import { useFreights } from "@/hooks/useFreights";
import { useCompanies } from "@/hooks/useCompanies";
import { useUrlState } from "@/hooks/useUrlState";
import { DataTable } from "@/components/ui/data-table";
import { SearchInput } from "@/components/ui/search-input";
import { Pagination } from "@/components/ui/pagination";
import { FreightStatusBadge } from "@/components/ui/freight-status-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ListSummary } from "@/components/ui/list-summary";
import { ComboboxSelect } from "@/components/form/combobox-select";
import { usePermissions } from "@/hooks/usePermissions";
import { formatFreightCode } from "@/lib/utils";
import { FreightReceiveModal } from "./FreightReceiveModal";
import type { Freight, FreightStatus } from "@/types/freight";
import type { Column } from "@/components/ui/data-table";

const RECEIVABLE_STATUSES = new Set<FreightStatus>([
  "DELIVERED",
  "PROOF_RECEIVED",
  "COMPLETED",
]);

type PaymentFilter = "" | "paid" | "unpaid";
const PAYMENT_FILTERS: { value: PaymentFilter; label: string }[] = [
  { value: "", label: "Todos" },
  { value: "unpaid", label: "A receber" },
  { value: "paid", label: "Recebidos" },
];

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: "", label: "Todos" },
  { value: "PENDING", label: "Pendente" },
  { value: "ASSIGNED", label: "Atribuído" },
  { value: "IN_TRANSIT", label: "Em trânsito" },
  { value: "DELIVERED", label: "Entregue" },
  { value: "PROOF_RECEIVED", label: "Canhoto recebido" },
  { value: "COMPLETED", label: "Concluído" },
];

function formatMoney(value: string | null) {
  if (!value) return "—";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(parseFloat(value));
}

function advanceAmount(row: Freight): string | null {
  if (!row.driverValue) return null;
  const percent = parseFloat(row.advancePercent);
  return (parseFloat(row.driverValue) * (percent / 100)).toFixed(2);
}

function buildColumns(
  isOperator: boolean,
  canReceive: boolean,
  onReceive: (row: Freight) => void,
): Column<Freight>[] {
  const valueColumns: Column<Freight>[] = isOperator
    ? [
        {
          key: "driverValue",
          header: "Valor motorista",
          render: (row) => formatMoney(row.driverValue),
        },
        {
          key: "advance",
          header: "Adiantamento",
          render: (row) => {
            const advance = advanceAmount(row);
            if (!advance) return "—";
            return (
              <span>
                {formatMoney(advance)}
                <span className="ml-1 text-xs text-muted-foreground">
                  ({parseFloat(row.advancePercent)}%)
                </span>
              </span>
            );
          },
        },
      ]
    : [
        {
          key: "freightValue",
          header: "Valor frete",
          render: (row) => formatMoney(row.freightValue),
        },
        {
          key: "driverValue",
          header: "Valor motorista",
          render: (row) => formatMoney(row.driverValue),
        },
      ];

  return [
    {
      key: "code",
      header: "Código",
      className: "w-20",
      render: (row) => (
        <span className="font-mono text-xs font-medium text-foreground">
          {formatFreightCode(row.code)}
        </span>
      ),
    },
    {
      key: "company",
      header: "Empresa",
      render: (row) => row.company.tradeName,
    },
    {
      key: "originCity",
      header: "Origem → Destino",
      render: (row) =>
        `${row.originCity}/${row.originState} → ${row.destinationCity}/${row.destinationState}`,
    },
    {
      key: "cargoDescription",
      header: "Carga",
      className: "hidden lg:table-cell",
      render: (row) =>
        row.cargoDescription.length > 40
          ? row.cargoDescription.slice(0, 40) + "…"
          : row.cargoDescription,
    },
    ...valueColumns,
    {
      key: "driver",
      header: "Motorista",
      render: (row) => row.driver?.name ?? "—",
    },
    {
      key: "status",
      header: "Status",
      render: (row) => (
        <FreightStatusBadge status={row.status as FreightStatus} />
      ),
    },
    {
      key: "payment",
      header: "Pagto",
      render: (row) => {
        if (row.invoice?.status === "PAID") {
          return (
            <Badge tone="success" icon={<CheckCircle2 size={11} />}>
              Recebido
            </Badge>
          );
        }
        if (RECEIVABLE_STATUSES.has(row.status)) {
          return <Badge tone="warning">A receber</Badge>;
        }
        return <span className="text-xs text-muted-foreground">—</span>;
      },
    },
    {
      key: "actions",
      header: "",
      className: "w-12",
      render: (row) => {
        const eligible =
          canReceive &&
          !row.invoiceId &&
          RECEIVABLE_STATUSES.has(row.status) &&
          row.freightValue &&
          Number(row.freightValue) > 0;
        if (!eligible) return null;
        return (
          <Button
            size="sm"
            variant="ghost"
            title="Receber pagamento"
            onClick={(e) => {
              e.stopPropagation();
              onReceive(row);
            }}
          >
            <CheckCircle2 size={14} />
          </Button>
        );
      },
    },
  ];
}

export function FreightListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useUrlState<string>("search", "");
  const [statusFilter, setStatusFilter] = useUrlState<string>("status", "");
  const [paymentFilter, setPaymentFilter] = useUrlState<PaymentFilter>("payment", "");
  const [companyFilter, setCompanyFilter] = useUrlState<string>("companyId", "");
  const [page, setPage] = useUrlState<number>("page", 1);
  const { has } = usePermissions();
  const isOperator = !has("freight.set_value");
  const canReceive = has("invoice.pay");
  const [receiveTarget, setReceiveTarget] = useState<Freight | null>(null);
  const columns = buildColumns(isOperator, canReceive, setReceiveTarget);

  const { data: companiesData } = useCompanies({ active: true, perPage: 200 });
  const companyOptions = [
    { value: "", label: "Todas as empresas" },
    ...(companiesData?.data ?? []).map((c) => ({
      value: c.id,
      label: c.tradeName,
    })),
  ];

  const { data, isLoading } = useFreights({
    search,
    status: statusFilter || undefined,
    paymentStatus: paymentFilter || undefined,
    companyId: companyFilter || undefined,
    page,
  });

  function handleSearch(v: string) {
    setSearch(v);
    setPage(1);
  }

  function handleStatus(v: string) {
    setStatusFilter(v);
    setPage(1);
  }

  function handlePayment(v: PaymentFilter) {
    setPaymentFilter(v);
    setPage(1);
  }

  function handleCompany(v: string) {
    setCompanyFilter(v);
    setPage(1);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Fretes</h1>
          <p className="text-sm text-muted-foreground">
            Gerenciamento de fretes
          </p>
        </div>
        <Button onClick={() => navigate("/freights/new")}>
          <Plus size={16} />
          Novo frete
        </Button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchInput
          value={search}
          onChange={handleSearch}
          placeholder="Buscar por código (ex: F-6), carga, origem, destino ou empresa…"
          className="flex-1"
        />
        <ComboboxSelect
          options={companyOptions}
          value={companyFilter}
          onChange={handleCompany}
          placeholder="Todas as empresas"
          searchPlaceholder="Buscar empresa..."
          className="w-full sm:w-52"
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex overflow-hidden rounded-md border border-input text-sm w-fit">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => handleStatus(f.value)}
              className={`px-3 py-1.5 transition-colors whitespace-nowrap ${
                statusFilter === f.value
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted/50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {!isOperator && (
          <div className="flex overflow-hidden rounded-md border border-input text-sm w-fit">
            {PAYMENT_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => handlePayment(f.value)}
                className={`px-3 py-1.5 transition-colors whitespace-nowrap ${
                  paymentFilter === f.value
                    ? "bg-foreground text-background"
                    : "text-muted-foreground hover:bg-muted/50"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {data && (
        <ListSummary
          metrics={
            isOperator
              ? [
                  {
                    label: "Pago a motoristas",
                    value: formatMoney(data.totals.driverValue),
                    tone: "neutral",
                    hint: `${data.total} ${data.total === 1 ? "frete" : "fretes"}`,
                  },
                ]
              : [
                  {
                    label: "Receita",
                    value: formatMoney(data.totals.freightValue),
                    tone: "neutral",
                    hint: `${data.total} ${data.total === 1 ? "frete" : "fretes"}`,
                  },
                  {
                    label: "Pago a motoristas",
                    value: formatMoney(data.totals.driverValue),
                    tone: "neutral",
                  },
                  {
                    label: "Margem",
                    value: formatMoney(data.totals.margin),
                    tone:
                      Number.parseFloat(data.totals.margin) >= 0 ? "positive" : "negative",
                  },
                ]
          }
        />
      )}

      <DataTable
        columns={columns}
        data={data?.data ?? []}
        keyExtractor={(row) => row.id}
        onRowClick={(row) => navigate(`/freights/${row.id}`)}
        isLoading={isLoading}
        emptyTitle="Nenhum frete encontrado"
        emptyDescription="Comece cadastrando o primeiro frete."
        emptyAction={
          <Button onClick={() => navigate("/freights/new")}>
            <Plus size={16} />
            Novo frete
          </Button>
        }
      />

      {data && (
        <Pagination
          page={data.page}
          totalPages={data.totalPages}
          total={data.total}
          perPage={data.perPage}
          onChange={setPage}
        />
      )}

      {receiveTarget && (
        <FreightReceiveModal
          freight={receiveTarget}
          open={!!receiveTarget}
          onClose={() => setReceiveTarget(null)}
        />
      )}
    </div>
  );
}
